import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sprout, ShieldCheck, Ship } from "lucide-react";

const PILLARS = [
  {
    icon: Sprout,
    title: "Farm-Level Sourcing",
    text: "We source directly from trusted Indian farms, ensuring every commodity is traceable from the soil to the shipment.",
  },
  {
    icon: ShieldCheck,
    title: "Quality & Sustainability",
    text: "Each product meets the highest standards of quality and sustainability, audited against international food-safety norms.",
  },
  {
    icon: Ship,
    title: "Global Logistics",
    text: "From Dehradun to the world's ports — sea and air freight managed with integrity and precision, end to end.",
  },
];

const FACTS = [
  { k: "07", v: "Export Product Lines" },
  { k: "2026", v: "Incorporated — MCA SPICE+" },
];

const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const ySmall = useTransform(scrollYProgress, [0, 1], [80, -60]);

  return (
    <section id="about" ref={ref} data-testid="about-section" className="relative bg-cream py-20 sm:py-28 overflow-hidden">
      <div className="px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* text column */}
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-[10px] sm:text-xs tracking-[0.4em] text-sunset uppercase mb-5"
            >
              01 — About Us
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl text-forest tracking-tight leading-[1.08]"
            >
              Where vision meets <span className="italic text-mud-accent">the world.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              data-testid="about-mission"
              className="mt-8 font-display text-lg sm:text-xl leading-relaxed text-charcoal/85 border-l-2 border-sunset pl-6"
            >
              We are dedicated to delivering premium global food sourcing and logistics.
              Our mission is to ensure that every agricultural product we import and
              export meets the highest standards of quality and sustainability, meeting
              the evolving demands of the international market with integrity and precision.
            </motion.p>

            <div className="mt-10 space-y-6">
              {PILLARS.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, delay: 0.1 + i * 0.12 }}
                  className="flex gap-5 group"
                >
                  <span className="shrink-0 w-12 h-12 rounded-full bg-forest text-gold flex items-center justify-center group-hover:bg-sunset group-hover:text-forest transition-colors duration-300">
                    <p.icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-grotesk font-semibold text-lg text-mud">{p.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-mutedtext max-w-md">{p.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* imagery column */}
          <div className="lg:col-span-6 relative h-[30rem] sm:h-[36rem] lg:h-[42rem]">
            <motion.div
              style={{ y: yMain }}
              initial={{ opacity: 0, scale: 0.94 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-0 right-0 w-[78%] h-[75%] rounded-t-full rounded-b-[2rem] overflow-hidden shadow-2xl"
            >
              <img
                data-testid="about-wheat-image"
                src="https://images.unsplash.com/photo-1657626625832-2c0851cdaa9b?q=80&w=1400&auto=format&fit=crop"
                alt="Golden wheat field at sunset in India"
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              style={{ y: ySmall }}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="absolute bottom-0 left-0 w-[52%] aspect-[4/3] rounded-2xl overflow-hidden ring-4 ring-cream shadow-2xl"
            >
              <img
                data-testid="about-agri-image"
                src="https://images.unsplash.com/photo-1723475158232-819e29803f4d?q=80&w=1000&auto=format&fit=crop"
                alt="Premium Indian agri produce — grains and commodities"
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="absolute bottom-[28%] left-[38%] bg-forest text-cream rounded-xl px-5 py-4 shadow-xl"
            >
              <p className="font-mono text-[10px] tracking-[0.3em] text-gold uppercase">Established</p>
              <p className="font-display text-2xl mt-1">28 · 09 · 2026</p>
              <p className="font-mono text-[10px] tracking-[0.2em] text-cream/60 uppercase mt-1">
                Dehradun, Uttarakhand
              </p>
            </motion.div>
          </div>
        </div>

        {/* facts strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="mt-16 sm:mt-20 grid grid-cols-2 border border-forest/15 rounded-2xl overflow-hidden bg-cream-card"
        >
          {FACTS.map((f, i) => (
            <div
              key={f.k + f.v}
              data-testid={`about-fact-${i}`}
              className={`p-6 sm:p-8 ${i !== 0 ? "border-l border-forest/15" : ""} ${
                i >= 2 ? "border-t lg:border-t-0 border-forest/15" : ""
              }`}
            >
              <p className="font-display text-3xl sm:text-4xl text-forest">{f.k}</p>
              <p className="mt-2 font-mono text-[10px] sm:text-xs tracking-[0.2em] text-mutedtext uppercase">
                {f.v}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
